/**
 * Payroll Settings permission helper.
 *
 * Accepted role strings (DB stores enum values, legacy DB may store enum keys):
 *   Super Admin  →  'Super Admin' | 'SUPER_ADMIN'
 *   Admin        →  'Admin'       | 'ADMIN'
 */

const SUPER_ADMIN_ROLES = ['Super Admin', 'SUPER_ADMIN'];
const ADMIN_ROLES = [
  'Admin',
  'ADMIN',
  'Branch Super Admin',
  'BRANCH_SUPER_ADMIN',
];

/**
 * Returns true if the user can edit Payroll Settings.
 *
 * Rules:
 *   1. Super Admin  → always allowed
 *   2. Admin + permission "payroll_settings_edit" → allowed
 *   3. Everyone else → read-only
 *
 * @param {string[]} roles       - user's roles array from localStorage / JWT
 * @param {string[]} permissions - user's permissions array from localStorage / JWT
 */
export function canEditPayrollSettings(roles = [], permissions = []) {
  if (!roles || roles.length === 0) return false;

  const isSuperAdmin = roles.some(r => SUPER_ADMIN_ROLES.includes(r));
  if (isSuperAdmin) return true;

  const isAdmin = roles.some(r => ADMIN_ROLES.includes(r));
  if (isAdmin && permissions.includes('payroll_settings_edit')) return true;

  return false;
}

/**
 * Returns true if the user can view ABA Pay Runs.
 *
 * Rules:
 *   1. Super Admin  → always allowed
 *   2. Admin + permission "aba_pay_runs_view" → allowed
 *   3. Everyone else → not visible
 */
export function canViewAbaPayRuns(roles = [], permissions = []) {
  if (!roles || roles.length === 0) return false;

  const isSuperAdmin = roles.some(r => SUPER_ADMIN_ROLES.includes(r));
  if (isSuperAdmin) return true;

  const isAdmin = roles.some(r => ADMIN_ROLES.includes(r));
  if (isAdmin && permissions.includes('aba_pay_runs_view')) return true;

  return false;
}

/**
 * Returns true if the user can view STP Submissions.
 *
 * Rules:
 *   1. Super Admin  → always allowed
 *   2. Admin        → always allowed
 *   3. Finance role → always allowed
 *   4. Everyone else → not visible
 */
export function canViewSTP(roles = []) {
  if (!roles || roles.length === 0) return false;

  return roles.some(r =>
    [...SUPER_ADMIN_ROLES, ...ADMIN_ROLES, 'Finance', 'FINANCE'].includes(r)
  );
}
